import { useEffect, useMemo, useRef, useState } from "react";
import {
  Clock,
  Glasses,
  Inbox,
  Loader2,
  Mail,
  MailOpen,
  Trash2,
  FileText,
  CheckCircle2,
  XCircle,
  UploadCloud,
  Search as SearchIcon,
  Filter,
  Download,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import notificationService, {
  NotificationItem,
  NotificationListParams,
} from "../../../services/notification.service";
import Loader from "../../../widgets/Loader";
import Dropdown, { DropdownOption } from "../../users/ui/Dropdown";
import toast from "react-hot-toast";

/**
 * Notification history tab — every filter, search, sort and page change
 * hits the backend. Layout still has the same toolbars; what changed is
 * that the data + total + unread counts come from the server, so the
 * pagination footer reflects the *real* total even when only one page is
 * loaded.
 *
 * Backend params sent on every fetch:
 *   $skip, $limit, is_read?, event_type?, date_from?, date_to?, search?
 * (older backends that don't handle the new params return everything and
 * the UI degrades to "page 1 of 1" gracefully.)
 */

const formatTimestamp = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

function eventIcon(eventType: string | undefined) {
  if (!eventType) return <FileText className="w-4 h-4 text-slate-500" />;
  const t = eventType.toLowerCase();
  if (t.includes("upload"))
    return <UploadCloud className="w-4 h-4 text-blue-500" />;
  if (t.includes("fail") || t.includes("error"))
    return <XCircle className="w-4 h-4 text-red-500" />;
  if (t.includes("complete") || t.includes("done"))
    return <CheckCircle2 className="w-4 h-4 text-emerald-500" />;
  return <FileText className="w-4 h-4 text-slate-500" />;
}

type StatusFilter = "all" | "unread" | "read";
type EventFilter = "all" | "upload" | "completed" | "failed";
type DateFilter = "all" | "today" | "7d" | "30d" | "custom";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

/**
 * Map our UI filter selections → backend query params. Status `unread`
 * sends `is_read=false`, `read` sends `is_read=true`, `all` omits the
 * field entirely (so the backend returns everything).
 */
function buildBackendParams(args: {
  status: StatusFilter;
  eventFilter: EventFilter;
  dateFilter: DateFilter;
  customStart: string;
  customEnd: string;
  search: string;
  page: number;
  pageSize: number;
}): NotificationListParams {
  const p: NotificationListParams = {
    skip: (args.page - 1) * args.pageSize,
    limit: args.pageSize,
  };
  if (args.status === "unread") p.is_read = false;
  else if (args.status === "read") p.is_read = true;
  if (args.eventFilter !== "all") p.event_type = args.eventFilter;
  if (args.search.trim()) p.search = args.search.trim();

  // Date range → ISO strings the backend can compare with.
  if (args.dateFilter !== "all") {
    let from: Date | null = null;
    let to: Date | null = null;
    const now = new Date();
    if (args.dateFilter === "today") {
      from = new Date();
      from.setHours(0, 0, 0, 0);
    } else if (args.dateFilter === "7d") {
      from = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    } else if (args.dateFilter === "30d") {
      from = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    } else if (args.dateFilter === "custom") {
      if (args.customStart) from = new Date(args.customStart);
      if (args.customEnd) {
        to = new Date(args.customEnd);
        to.setHours(23, 59, 59, 999);
      }
    }
    if (from) p.date_from = from.toISOString();
    if (to) p.date_to = to.toISOString();
  }
  return p;
}

function MyNotificationsTab() {
  // Data
  const [rows, setRows] = useState<NotificationItem[]>([]);
  const [total, setTotal] = useState(0);
  // Grand totals across all the user's notifications, independent of the
  // active filter — used for the dropdown count labels and the Mark-all
  // disabled state so they show the real numbers even while a filter is
  // active. `total` (above) is the filtered count and drives pagination.
  const [grandCounts, setGrandCounts] = useState({ all: 0, unread: 0, read: 0 });
  const [loading, setLoading] = useState(true);
  const [markAllLoading, setMarkAllLoading] = useState(false);

  // Filters
  const [status, setStatus] = useState<StatusFilter>("all");
  const [eventFilter, setEventFilter] = useState<EventFilter>("all");
  const [dateFilter, setDateFilter] = useState<DateFilter>("all");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [searchInput, setSearchInput] = useState(""); // raw input
  const [search, setSearch] = useState(""); // debounced value

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Bulk select — keyed by id, persists across page changes within a session
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // ─── Debounce the search input so we don't hammer the backend ────────
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setSearch(searchInput), 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput]);

  // ─── Reset to page 1 whenever any filter / search changes ────────────
  useEffect(() => {
    setPage(1);
  }, [status, eventFilter, dateFilter, customStart, customEnd, search, pageSize]);

  // ─── Backend fetch on every filter or page change ────────────────────
  // Each render the latest params object is sent. The cancelled flag guards
  // against stale responses arriving out of order.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = buildBackendParams({
          status,
          eventFilter,
          dateFilter,
          customStart,
          customEnd,
          search,
          page,
          pageSize,
        });
        const res = await notificationService.getNotificationList(params);
        if (cancelled) return;
        setRows(res.data);
        setTotal(res.total_count);
        setGrandCounts(res.counts);
      } catch (e) {
        if (!cancelled) toast.error("Failed to load notifications");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    status,
    eventFilter,
    dateFilter,
    customStart,
    customEnd,
    search,
    page,
    pageSize,
  ]);

  // ─── Derived pagination state ────────────────────────────────────────
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);
  const startItem = total === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const endItem = Math.min(safePage * pageSize, total);

  // Status pill counts — always the user's grand totals (independent of
  // the active filter) so the labels read correctly no matter which value
  // is selected. `total` only reflects the FILTERED set and is used for
  // the pagination footer / "Showing X of N" hint.
  const statusPills: { id: StatusFilter; label: string; icon: any; count: number }[] = [
    { id: "all",    label: "All",    icon: Inbox,    count: grandCounts.all },
    { id: "unread", label: "Unread", icon: Mail,     count: grandCounts.unread },
    { id: "read",   label: "Read",   icon: MailOpen, count: grandCounts.read },
  ];

  // Build the DropdownOption[] arrays for the custom dropdowns. Each one
  // shows an icon + label + (for Status) the live count next to the label.
  const statusDropdownOptions: DropdownOption[] = statusPills.map((p) => ({
    value: p.id,
    label: `${p.label} (${p.count})`,
    icon: <p.icon className="w-3.5 h-3.5" />,
  }));
  const eventDropdownOptions: DropdownOption[] = [
    { value: "all",       label: "All events",       icon: <Filter className="w-3.5 h-3.5" /> },
    { value: "upload",    label: "Uploads",          icon: <UploadCloud className="w-3.5 h-3.5 text-blue-500" /> },
    { value: "completed", label: "Processing done",  icon: <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> },
    { value: "failed",    label: "Processing failed",icon: <XCircle className="w-3.5 h-3.5 text-red-500" /> },
  ];
  const dateDropdownOptions: DropdownOption[] = [
    { value: "all",    label: "All time",      icon: <Clock className="w-3.5 h-3.5" /> },
    { value: "today",  label: "Today",         icon: <Clock className="w-3.5 h-3.5" /> },
    { value: "7d",     label: "Last 7 days",   icon: <Clock className="w-3.5 h-3.5" /> },
    { value: "30d",    label: "Last 30 days",  icon: <Clock className="w-3.5 h-3.5" /> },
    { value: "custom", label: "Custom range",  icon: <Clock className="w-3.5 h-3.5" /> },
  ];

  // Refetch helper used after a mutation (read / bulk delete) so the row
  // counts stay accurate without a full re-render dance.
  const refetch = async () => {
    const params = buildBackendParams({
      status,
      eventFilter,
      dateFilter,
      customStart,
      customEnd,
      search,
      page,
      pageSize,
    });
    const res = await notificationService.getNotificationList(params);
    setRows(res.data);
    setTotal(res.total_count);
    setGrandCounts(res.counts);
  };

  // ─── Row + bulk actions ──────────────────────────────────────────────
  const handleClick = async (id: string) => {
    try {
      await notificationService.postNotificationRead({ ids: [id] });
      // Optimistic local update + revalidate counts from server.
      setRows((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
      setGrandCounts((c) => ({
        ...c,
        unread: Math.max(0, c.unread - 1),
        read: c.read + 1,
      }));
      await refetch();
    } catch {
      /* ignore */
    }
  };

  const handleMarkAllRead = async () => {
    if (grandCounts.unread === 0) return;
    setMarkAllLoading(true);
    try {
      // Single backend call — marks every unread notification for the
      // caller in one shot (no need to fetch the id list first).
      await notificationService.markAllAsRead();
      toast.success("All notifications marked as read");
      await refetch();
    } catch {
      toast.error("Failed to mark all as read");
    } finally {
      setMarkAllLoading(false);
    }
  };

  const allOnPageSelected = rows.length > 0 && rows.every((r) => selectedIds.has(r.id));

  const togglePageSelection = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) {
        rows.forEach((r) => next.delete(r.id));
      } else {
        rows.forEach((r) => next.add(r.id));
      }
      return next;
    });
  };
  const toggleRow = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  const bulkMarkRead = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    try {
      await notificationService.postNotificationRead({ ids });
      toast.success(`Marked ${ids.length} as read`);
      clearSelection();
      await refetch();
    } catch {
      toast.error("Bulk mark-read failed");
    }
  };

  const bulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!confirm(`Delete ${ids.length} notification${ids.length === 1 ? "" : "s"}?`)) return;
    try {
      await notificationService.deleteNotifications(ids);
      toast.success(`Deleted ${ids.length} notification${ids.length === 1 ? "" : "s"}`);
      clearSelection();
      await refetch();
    } catch {
      toast.error("Bulk delete failed");
    }
  };

  const bulkExport = () => {
    const exportRows = rows.filter((n) => selectedIds.has(n.id));
    if (exportRows.length === 0) return;
    const header = ["id", "event_type", "username", "message", "is_read", "createdAt"];
    const csv = [
      header.join(","),
      ...exportRows.map((r) =>
        [
          r.id,
          r.event_type || "",
          r.user?.username || "",
          (r.message || "").replace(/"/g, '""'),
          r.is_read ? "true" : "false",
          r.createdAt,
        ]
          .map((v) => `"${String(v).replace(/\n/g, " ")}"`)
          .join(","),
      ),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `notifications_${Date.now()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${exportRows.length} rows`);
  };

  const hasActiveFilters = useMemo(
    () =>
      !!search ||
      status !== "all" ||
      eventFilter !== "all" ||
      dateFilter !== "all" ||
      !!customStart ||
      !!customEnd,
    [search, status, eventFilter, dateFilter, customStart, customEnd],
  );

  const resetFilters = () => {
    setSearchInput("");
    setSearch("");
    setStatus("all");
    setEventFilter("all");
    setDateFilter("all");
    setCustomStart("");
    setCustomEnd("");
  };

  // ─── Render ──────────────────────────────────────────────────────────
  return (
    <div className="space-y-3">
      {/* ── Unified toolbar — Search · Status · Event · Date · Mark-all ─ */}
      <div className="bg-slate-50 border border-slate-200 rounded-md p-3 flex flex-wrap items-end gap-3">
        {/* Search */}
        <div className="flex-1 min-w-[220px]">
          <label className="block text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">
            Search
          </label>
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by user, message or event type…"
              className="w-full pl-9 pr-8 py-2 text-sm bg-white border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            {searchInput && (
              <button
                type="button"
                onClick={() => setSearchInput("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                title="Clear"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Status — custom dropdown with icons + live counts. Defaults to "all". */}
        <div className="w-44">
          <label className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">
            <Mail className="w-3 h-3" />
            Status
          </label>
          <Dropdown
            options={statusDropdownOptions}
            value={status}
            onChange={(v) => setStatus(v as StatusFilter)}
            size="md"
            className="w-full"
            ariaLabel="Filter by read status"
          />
        </div>

        {/* Event — custom dropdown with kind-coloured icons. Defaults to "all". */}
        <div className="w-52">
          <label className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">
            <Filter className="w-3 h-3" />
            Event
          </label>
          <Dropdown
            options={eventDropdownOptions}
            value={eventFilter}
            onChange={(v) => setEventFilter(v as EventFilter)}
            size="md"
            className="w-full"
            ariaLabel="Filter by event type"
          />
        </div>

        {/* Date — custom dropdown. "Custom range" reveals date pickers below. */}
        <div className="w-44">
          <label className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">
            <Clock className="w-3 h-3" />
            Date
          </label>
          <Dropdown
            options={dateDropdownOptions}
            value={dateFilter}
            onChange={(v) => setDateFilter(v as DateFilter)}
            size="md"
            className="w-full"
            ariaLabel="Filter by date range"
          />
        </div>

        {/* Mark all read button — right-aligned with the toolbar */}
        <button
          type="button"
          onClick={handleMarkAllRead}
          disabled={grandCounts.unread === 0 || markAllLoading}
          className="inline-flex items-center gap-2 px-3 py-2 text-xs font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title={
            grandCounts.unread === 0
              ? "Nothing unread"
              : `Mark ${grandCounts.unread} as read`
          }
        >
          {markAllLoading ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Glasses className="w-3.5 h-3.5" />
          )}
          Mark all as read
          {grandCounts.unread > 0 && (
            <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-white/20 rounded">
              {grandCounts.unread}
            </span>
          )}
        </button>

        {dateFilter === "custom" && (
          <div className="basis-full flex items-center gap-2 pt-1">
            <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
              Custom range
            </span>
            <input
              type="date"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              className="px-2 py-1.5 text-sm bg-white border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <span className="text-slate-400 text-sm">→</span>
            <input
              type="date"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              className="px-2 py-1.5 text-sm bg-white border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        )}

        {hasActiveFilters && (
          <button
            type="button"
            onClick={resetFilters}
            className="text-xs text-slate-600 hover:text-slate-900 underline underline-offset-4 self-end pb-2"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* ── Bulk action bar (only when something selected) ───────────── */}
      {selectedIds.size > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-md px-3 py-2 flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-blue-800">
            {selectedIds.size} selected
          </span>
          <div className="flex items-center gap-1.5 ml-auto">
            <button
              type="button"
              onClick={bulkMarkRead}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-white border border-blue-300 text-blue-700 rounded-md hover:bg-blue-100"
            >
              <Glasses className="w-3.5 h-3.5" />
              Mark read
            </button>
            <button
              type="button"
              onClick={bulkExport}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-white border border-slate-300 text-slate-700 rounded-md hover:bg-slate-50"
            >
              <Download className="w-3.5 h-3.5" />
              Export CSV
            </button>
            <button
              type="button"
              onClick={bulkDelete}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-white border border-red-300 text-red-700 rounded-md hover:bg-red-50"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Delete
            </button>
            <button
              type="button"
              onClick={clearSelection}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-slate-600 hover:text-slate-900"
            >
              <X className="w-3.5 h-3.5" />
              Clear
            </button>
          </div>
        </div>
      )}

      {/* ── List ──────────────────────────────────────────────────────── */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader />
        </div>
      ) : total === 0 ? (
        <div className="text-center py-12 text-slate-500 text-sm border border-dashed border-slate-200 rounded-md">
          <Inbox className="w-10 h-10 mx-auto mb-2 text-slate-300" />
          {hasActiveFilters || status !== "all"
            ? "No notifications match your filters."
            : "No notifications yet."}
        </div>
      ) : (
        <div className="border border-slate-200 rounded-md overflow-hidden">
          {/* Header with select-all */}
          <div className="flex items-center gap-3 px-4 py-2 bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
            <input
              type="checkbox"
              checked={allOnPageSelected}
              onChange={togglePageSelection}
              className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
              title={allOnPageSelected ? "Deselect all on this page" : "Select all on this page"}
            />
            <span className="ml-1">Event</span>
            <span className="ml-auto pr-1">When</span>
          </div>

          <ul className="divide-y divide-slate-100 bg-white">
            {rows.map((item) => {
              const selected = selectedIds.has(item.id);
              return (
                <li
                  key={item.id}
                  className={`flex items-start gap-3 px-4 py-3 transition cursor-pointer ${
                    selected
                      ? "bg-blue-50/70 hover:bg-blue-50"
                      : item.is_read
                        ? "bg-white hover:bg-slate-50"
                        : "bg-blue-50/30 hover:bg-blue-50/50"
                  }`}
                  onClick={() => !item.is_read && handleClick(item.id)}
                >
                  {/* Checkbox */}
                  <div
                    className="pt-1 flex-shrink-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleRow(item.id);
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selected}
                      readOnly
                      className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                    />
                  </div>

                  {/* Unread dot */}
                  <div className="w-2 flex-shrink-0 pt-2">
                    {!item.is_read && (
                      <span className="block w-2 h-2 rounded-full bg-blue-600" />
                    )}
                  </div>

                  {/* Event icon */}
                  <div className="flex-shrink-0 w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center mt-0.5">
                    {eventIcon(item.event_type)}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className={`text-sm font-medium ${
                          item.is_read ? "text-slate-700" : "text-slate-900"
                        }`}
                      >
                        {item.user?.username || "System"}
                      </span>
                      {item.event_type && (
                        <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium bg-slate-100 text-slate-600 rounded uppercase tracking-wider">
                          {item.event_type.replace(/[._]/g, " ")}
                        </span>
                      )}
                    </div>
                    <p
                      className={`text-sm mt-0.5 ${
                        item.is_read ? "text-slate-500" : "text-slate-800"
                      }`}
                    >
                      {item.message}
                    </p>
                  </div>

                  {/* Right-side meta */}
                  <div className="flex flex-col items-end gap-2 flex-shrink-0">
                    <span className="flex items-center gap-1 text-[11px] text-slate-500 whitespace-nowrap">
                      <Clock className="w-3 h-3" />
                      {formatTimestamp(item.createdAt)}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>

          {/* ── Pagination footer ─────────────────────────────────── */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-slate-100 bg-slate-50/50">
            <div className="flex items-center gap-3 text-xs text-slate-600">
              <span>
                Showing <span className="font-medium text-slate-900">{startItem}</span>
                {"–"}
                <span className="font-medium text-slate-900">{endItem}</span>
                {" of "}
                <span className="font-medium text-slate-900">{total}</span>
              </span>
              <span className="text-slate-300">|</span>
              <label className="inline-flex items-center gap-1.5">
                <span>Show</span>
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="px-1.5 py-0.5 border border-slate-300 rounded-md bg-white text-xs"
                >
                  {PAGE_SIZE_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                <span>per page</span>
              </label>
            </div>

            <div className="inline-flex items-center gap-1">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={safePage <= 1}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-white border border-slate-300 text-slate-700 rounded-md hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
                Prev
              </button>
              <span className="px-2 text-xs text-slate-600">
                Page <span className="font-medium text-slate-900">{safePage}</span> of{" "}
                <span className="font-medium text-slate-900">{totalPages}</span>
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={safePage >= totalPages}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-white border border-slate-300 text-slate-700 rounded-md hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MyNotificationsTab;
